<template>
  <div
    class="comment-item group p-3 transition-colors duration-200"
    :class="{
      'bg-blue-lighter border-l-4 border-blue': isBeingEdited,
      'bg-white hover:bg-gray-lightest': !isBeingEdited,
    }"
    @mouseenter="showMenu = true"
    @mouseleave="showMenu = false"
  >
    <div class="comment-header mb-3 flex justify-between items-start">
      <div class="min-w-0 flex-1 mr-3">
        <div class="comment-author font-bold text-sm break-words">
          {{ getDisplayName(comment.user) }}
          <span v-if="isBeingEdited" class="text-blue text-xs">
            ({{ $t('brand-surveys.survey-question-modal.currently-editing') }})
          </span>
        </div>
        <div
          class="comment-date text-xs text-gray-darker break-words"
          style="font-size: 12px"
        >
          {{ formatDate(comment.createdAt) }}
          <span v-if="isEdited" class="text-gray-darker italic">
            ({{ $t('common.edited') }})
          </span>
        </div>
      </div>
      <div
        v-show="showMenu && !isBeingEdited && canModifyComment"
        class="flex-shrink-0"
      >
        <popover>
          <template #trigger="{ attrs, togglePopoverVisibility }">
            <btn
              v-bind="attrs"
              :theme="BtnTheme.NONE"
              size="xs"
              class="w-6 h-7 bg-white p-0 z-10 rounded border border-gray-light"
              @click="togglePopoverVisibility"
            >
              <icon :icon="mdiDotsVertical" :size="IconSize.SM" />
            </btn>
          </template>
          <showing-menu
            class="text-sm px-2 text-left"
            :items="menuActions"
            menu-class="min-w-32"
          />
        </popover>
      </div>
    </div>

    <wysiwyg-input
      :value="comment.content"
      readonly
      hide-toolbar
      borderless
      dense
      transparent
      content-class="p-0 break-words"
      class="text-sm overflow-hidden"
    />
  </div>
</template>

<script>
import { computed, defineComponent, ref } from '@vue/composition-api';
import { WysiwygInput } from '@/components/WysiwygInput';
import { dateTime } from '@/util/formatters';
import { Icon, IconSize } from '@/components/Icon';
import { Btn, BtnTheme } from '@/components/Button';
import { mdiDotsVertical } from '@mdi/js';
import { Popover } from '@/components/Popover';
import ShowingMenu from '@/components/EllipsisMenu/ShowingMenu.vue';
import { useAuthUser } from '@/composables/use-auth-user';

export default defineComponent({
  name: 'SurveyQuestionComment',
  components: {
    WysiwygInput,
    ShowingMenu,
    Popover,
    Btn,
    Icon,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    isBeingEdited: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const showMenu = ref(false);
    const { authUserId } = useAuthUser();

    const isEdited = computed(() => {
      const created = new Date(props.comment.createdAt);
      const updated = new Date(props.comment.updatedAt);
      return updated.getTime() > created.getTime();
    });

    const canModifyComment = computed(() => {
      return (
        props.comment.userId === authUserId.value ||
        props.comment.user?.id === authUserId.value
      );
    });

    const formatDate = (dateString) => {
      return dateTime(dateString);
    };

    const getDisplayName = (user) => {
      if (!user) return '';
      if (user.fullName) return user.fullName;
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      return `${firstName} ${lastName}`.trim();
    };

    const menuActions = computed(() => {
      const actions = {};

      if (canModifyComment.value) {
        actions.edit = {
          name: ctx.root.$t('common.edit'),
          handler() {
            ctx.emit('edit-comment', props.comment);
          },
        };
        actions.delete = {
          name: ctx.root.$t('common.delete'),
          handler() {
            ctx.emit('delete-comment', props.comment);
          },
        };
      }

      return actions;
    });

    return {
      showMenu,
      isEdited,
      canModifyComment,
      formatDate,
      getDisplayName,
      menuActions,
      mdiDotsVertical,
      IconSize,
      BtnTheme,
    };
  },
});
</script>
